import React, { useEffect, useState } from "react";
import { Dropdown, Checkbox, Space, Row, Col, ConfigProvider } from "antd";
import * as Icons from "assets/icons";
import useThemeStore from "(store)/themeStore";
import "styles/FilterDropdown.css";

const CategoryFilterDropdown = ({
  columnsData,
  onFilterChange = () => {},
  isDraft = false,
}) => {
  const [columns, setColumns] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const theme = useThemeStore((state) => state.theme);

  // Check if any column is visible (i.e., any filter is applied)
  const anyFilterApplied = columns.some((column) => column.visible);

  useEffect(() => {
    setColumns(columnsData);
  }, [columnsData]);

  // Handle checkbox change and update selected filters as a comma-separated string
  const onChange = (checkedValues) => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => ({
        ...column,
        visible: checkedValues.includes(column.id),
      }))
    );

    // Create a string of selected column labels
    const selectedFilters = columns
      .filter((column) => checkedValues.includes(column.id))
      .map((col) => col.value);

    // Send the selected filter string back to the parent component
    onFilterChange(selectedFilters);
  };

  const handleRowClick = (value, e) => {
    e.stopPropagation(); // Stop the event from closing the dropdown
    setColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.id === value ? { ...column, visible: !column.visible } : column
      )
    );
  };

  const generateCheckboxOptions = () =>
    columns.map((column) => ({
      label: column.label,
      value: column.id,
    }));

  const handleClearFilters = () => {
    if (anyFilterApplied || isDraft) {
      setColumns((prevColumns) =>
        prevColumns.map((column) => ({ ...column, visible: false }))
      );
      onFilterChange([], true);
    }
  };

  const generateMenuItems = () => {
    let items = [];

    // Title Section
    items.push({
      key: "filter-title",
      label: (
        <strong className="block text-sm font-normal text-gray-400 px-1 mb-2 sticky top-0 bg-white dark:bg-[#1f1f1f] z-10">
          FILTER:
        </strong>
      ),
      type: "group",
    });

    // Clear Filters Button
    items.push({
      key: "clear-filters",
      label: (
        <div
          onClick={handleClearFilters}
          className={`text-white py-1 cursor-pointer rounded-md w-1/2 text-center sticky top-8 z-10 ${
            anyFilterApplied || isDraft
              ? "bg-secondary bg-gradient-to-b from-[#1ab5c0] to-[#1996a6] hover:from-[#1996a6] hover:to-[#1a7684]"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Clear Filters
        </div>
      ),
    });

    // Checkbox Options (Scrollable Section)
    items.push({
      key: "checkbox-options",
      label: (
        <div
          style={{
            maxHeight: 185,
            overflowY: "auto",
            scrollbarWidth: "none",
            width: "100%", // Use full width of parent container
            minWidth: "190px", // Minimum width to maintain readability
            maxWidth: "100%", // Prevents overflow outside the parent container
          }}
        >
          <Checkbox.Group
            value={columns.filter((col) => col.visible).map((col) => col.id)}
            onChange={onChange}
            style={{ width: "100%" }}
            className="dark:text-white"
          >
            <Row gutter={[0, 6]}>
              {generateCheckboxOptions().map((option, index) => (
                <Col span={24} key={index}>
                  {option.label && (
                    <>
                      <label
                        className="flex justify-between items-center px-1 py-[2px] rounded-md cursor-pointer dark:hover:bg-gray-600 hover:bg-[#f5f5f5]"
                        onClick={(e) => handleRowClick(option.value, e)}
                      >
                        <span className="text-sm">{option.label}</span>

                        <Checkbox
                          value={option.value}
                          checked={
                            columns.find((col) => col.id === option.value)
                              ?.visible
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </label>
                    </>
                  )}
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </div>
      ),
    });

    return items;
  };

  const menu = {
    items: generateMenuItems(),
    className:
      "p-2 border dark:border-gray-800 bg-white rounded-lg no-hover-bg",
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Checkbox: {
            colorPrimary: "#1996a6",
            colorPrimaryHover: "#1996a6",
          },
          Dropdown: {
            colorBgElevated: theme === "dark" ? "#1f1f1f" : "white",
          },
        },
      }}
    >
      <Dropdown
        menu={menu}
        trigger={["click"]}
        overlayClassName="w-56 hover:bg-transparent"
        onOpenChange={handleOpenChange}
        className={`border rounded-lg ${
          isOpen ? "border-[#2397a5]" : "border-gray-300 dark:border-gray-800"
        } hover:border-[#2397a5] hover:ring-[#2397a5] hover:outline-none`}
      >
        <div className="cursor-pointer flex items-center justify-center hover:bg-transparent">
          <Space className="py-[5px] px-3">
            <span>
              <Icons.FilterIcon
                color={
                  isOpen
                    ? "#2397a5"
                    : theme === "dark"
                    ? "#d1d5db"
                    : "#91919280"
                }
                height={"28px"}
                width={"30px"}
              />
            </span>
            <span className="text-gray-500 text-[1em] dark:text-gray-300 mt-1">
              Filter
            </span>
          </Space>
        </div>
      </Dropdown>
    </ConfigProvider>
  );
};

export default CategoryFilterDropdown;
